// ═══════════════════════════════════════════════════════════════
// Director Task Service — CRUD for director_tasks table
// Path: src/services/taskService.js
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";
import { computeNextDueDate, recurrenceShouldStop } from "../constants/taskTypes";

const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    directorName: row.director_name,
    title: row.title,
    notes: row.notes || "",
    category: row.category || "general",
    priority: row.priority || "normal",
    status: row.status || "open",
    dueDate: row.due_date,
    completedAt: row.completed_at,
    isAuto: row.is_auto || false,
    sourceMove: row.source_move,
    sourceReportId: row.source_report_id || null,
    recurrenceId: row.recurrence_id || null,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const toDb = (data) => {
  const row = {};
  if (data.memberId !== undefined) row.member_id = data.memberId;
  if (data.directorName !== undefined) row.director_name = data.directorName;
  if (data.title !== undefined) row.title = data.title;
  if (data.notes !== undefined) row.notes = data.notes;
  if (data.category !== undefined) row.category = data.category;
  if (data.priority !== undefined) row.priority = data.priority;
  if (data.status !== undefined) row.status = data.status;
  if (data.dueDate !== undefined) row.due_date = data.dueDate;
  if (data.completedAt !== undefined) row.completed_at = data.completedAt;
  if (data.isAuto !== undefined) row.is_auto = data.isAuto;
  if (data.sourceMove !== undefined) row.source_move = data.sourceMove;
  if (data.sourceReportId !== undefined) row.source_report_id = data.sourceReportId;
  if (data.recurrenceId !== undefined) row.recurrence_id = data.recurrenceId;
  return row;
};

// ── task_recurrences mapping ─────────────────────────────────────

const recurrenceFromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    title: row.title,
    notes: row.notes || "",
    category: row.category || "general",
    priority: row.priority || "normal",
    memberId: row.member_id,
    directorName: row.director_name,
    frequency: row.frequency,
    intervalValue: row.interval_value || 1,
    endType: row.end_type || "never",
    endCount: row.end_count,
    endDate: row.end_date,
    occurrencesSpawned: row.occurrences_spawned || 0,
    isActive: row.is_active !== false,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

const recurrenceToDb = (data) => {
  const row = {};
  if (data.title !== undefined) row.title = data.title;
  if (data.notes !== undefined) row.notes = data.notes;
  if (data.category !== undefined) row.category = data.category;
  if (data.priority !== undefined) row.priority = data.priority;
  if (data.memberId !== undefined) row.member_id = data.memberId;
  if (data.directorName !== undefined) row.director_name = data.directorName;
  if (data.frequency !== undefined) row.frequency = data.frequency;
  if (data.intervalValue !== undefined) row.interval_value = data.intervalValue;
  if (data.endType !== undefined) row.end_type = data.endType;
  if (data.endCount !== undefined) row.end_count = data.endCount;
  if (data.endDate !== undefined) row.end_date = data.endDate;
  if (data.occurrencesSpawned !== undefined)
    row.occurrences_spawned = data.occurrencesSpawned;
  if (data.isActive !== undefined) row.is_active = data.isActive;
  return row;
};

export const taskService = {
  // Get all tasks visible to current user (RLS filters automatically)
  // Loads open tasks + completed tasks from last 30 days
  async getAll() {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data, error } = await supabase
      .from("director_tasks")
      .select("*")
      .or(`status.eq.open,completed_at.gte.${thirtyDaysAgo.toISOString()}`)
      .order("due_date", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (error) {
      debugLog.error("taskService.getAll", error);
      throw error;
    }
    debugLog.data("Tasks loaded", { count: (data || []).length });
    return (data || []).map(fromDb);
  },

  // Get tasks for a specific director
  async getByDirector(directorName) {
    const { data, error } = await supabase
      .from("director_tasks")
      .select("*")
      .eq("director_name", directorName)
      .eq("status", "open")
      .order("due_date", { ascending: true, nullsFirst: false });

    if (error) {
      debugLog.error("taskService.getByDirector", error);
      throw error;
    }
    return (data || []).map(fromDb);
  },

  // Get tasks linked to a specific donor
  async getByMember(memberId) {
    const { data, error } = await supabase
      .from("director_tasks")
      .select("*")
      .eq("member_id", memberId)
      .order("created_at", { ascending: false });

    if (error) {
      debugLog.error("taskService.getByMember", error);
      throw error;
    }
    return (data || []).map(fromDb);
  },

  // Create a task. If taskData.recurrence is provided (with at least
  // a frequency !== "none"), a task_recurrences row is inserted first
  // and the first instance is stamped with its id.
  async create(taskData) {
    let recurrenceId = null;

    if (
      taskData.recurrence &&
      taskData.recurrence.frequency &&
      taskData.recurrence.frequency !== "none"
    ) {
      const rRow = recurrenceToDb({
        title: taskData.title,
        notes: taskData.notes,
        category: taskData.category,
        priority: taskData.priority,
        memberId: taskData.memberId,
        directorName: taskData.directorName,
        frequency: taskData.recurrence.frequency,
        intervalValue: taskData.recurrence.intervalValue || 1,
        endType: taskData.recurrence.endType || "never",
        endCount: taskData.recurrence.endCount || null,
        endDate: taskData.recurrence.endDate || null,
        occurrencesSpawned: 1, // the instance we're about to insert counts
        isActive: true,
      });
      rRow.updated_at = new Date().toISOString();
      const { data: rData, error: rError } = await supabase
        .from("task_recurrences")
        .insert(rRow)
        .select()
        .single();
      if (rError) {
        debugLog.error("taskService.create(recurrence)", rError);
        throw rError;
      }
      recurrenceId = rData.id;
    }

    const row = toDb({ ...taskData, recurrenceId });
    row.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from("director_tasks")
      .insert(row)
      .select()
      .single();

    if (error) {
      debugLog.error("taskService.create", error);
      throw error;
    }
    debugLog.action("Task created", {
      title: data.title,
      id: data.id,
      recurring: recurrenceId !== null,
    });
    return fromDb(data);
  },

  // Update a task (partial)
  async update(id, updates) {
    const row = toDb(updates);
    row.updated_at = new Date().toISOString();

    const { data, error } = await supabase
      .from("director_tasks")
      .update(row)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      debugLog.error("taskService.update", error);
      throw error;
    }
    debugLog.action("Task updated", { id });
    return fromDb(data);
  },

  // Complete a task. If the task is part of a recurring series and
  // the series is still active + end condition not met, spawn the
  // next instance. Spawn errors are logged but not raised so the
  // completion write itself always succeeds.
  async complete(id) {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("director_tasks")
      .update({ status: "completed", completed_at: now, updated_at: now })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      debugLog.error("taskService.complete", error);
      throw error;
    }
    debugLog.action("Task completed", { id });

    const completed = fromDb(data);
    let spawned = null;
    if (completed.recurrenceId) {
      try {
        spawned = await this.spawnNextOccurrence(completed);
      } catch (spawnErr) {
        debugLog.error("taskService.spawnNextOccurrence (ignored)", spawnErr);
      }
    }
    return { task: completed, spawned };
  },

  // Load a recurrence template by id
  async getRecurrence(recurrenceId) {
    const { data, error } = await supabase
      .from("task_recurrences")
      .select("*")
      .eq("id", recurrenceId)
      .maybeSingle();
    if (error) {
      debugLog.error("taskService.getRecurrence", error);
      throw error;
    }
    return recurrenceFromDb(data);
  },

  // Spawn the next instance in a series. Internal helper invoked
  // from complete(). Returns the newly-inserted task or null when
  // the series has ended or been deactivated.
  async spawnNextOccurrence(completedTask) {
    if (!completedTask?.recurrenceId) return null;
    const recurrence = await this.getRecurrence(completedTask.recurrenceId);
    if (!recurrence) return null;
    if (recurrenceShouldStop(recurrence)) {
      debugLog.action("Recurrence stopped — no spawn", {
        recurrenceId: recurrence.id,
        spawned: recurrence.occurrencesSpawned,
        endType: recurrence.endType,
      });
      return null;
    }

    const nextDue = computeNextDueDate(
      completedTask.dueDate,
      recurrence.frequency,
      recurrence.intervalValue
    );

    const nextRow = toDb({
      title: recurrence.title,
      notes: recurrence.notes,
      category: recurrence.category,
      priority: recurrence.priority,
      memberId: recurrence.memberId,
      directorName: recurrence.directorName,
      status: "open",
      dueDate: nextDue,
      recurrenceId: recurrence.id,
      isAuto: false,
    });
    nextRow.updated_at = new Date().toISOString();

    const { data: inserted, error: insertErr } = await supabase
      .from("director_tasks")
      .insert(nextRow)
      .select()
      .single();
    if (insertErr) {
      debugLog.error("taskService.spawnNextOccurrence(insert)", insertErr);
      throw insertErr;
    }

    await supabase
      .from("task_recurrences")
      .update({
        occurrences_spawned: (recurrence.occurrencesSpawned || 0) + 1,
        updated_at: new Date().toISOString(),
      })
      .eq("id", recurrence.id);

    debugLog.action("Recurrence spawned", {
      recurrenceId: recurrence.id,
      newTaskId: inserted.id,
      nextDue,
    });
    return fromDb(inserted);
  },

  // Deactivate a series. Used when an unfinished instance is deleted
  // or when the user explicitly ends the series.
  async pauseRecurrence(recurrenceId) {
    if (!recurrenceId) return;
    const { error } = await supabase
      .from("task_recurrences")
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq("id", recurrenceId);
    if (error) {
      debugLog.error("taskService.pauseRecurrence", error);
      throw error;
    }
    debugLog.action("Recurrence paused", { recurrenceId });
  },

  // Admin "Edit series" escape hatch. Writes template fields on the
  // recurrence row only; existing instances are untouched.
  async updateSeries(recurrenceId, updates) {
    if (!recurrenceId) return null;
    const row = recurrenceToDb(updates);
    row.updated_at = new Date().toISOString();
    const { data, error } = await supabase
      .from("task_recurrences")
      .update(row)
      .eq("id", recurrenceId)
      .select()
      .single();
    if (error) {
      debugLog.error("taskService.updateSeries", error);
      throw error;
    }
    debugLog.action("Recurrence series updated", { recurrenceId });
    return recurrenceFromDb(data);
  },

  // Same surface as getAll but without the 30-day cap on completed
  // tasks. Used by TasksView archive toggle only — drawer and My Day
  // keep the fast capped path.
  async getAllWithArchive() {
    const { data, error } = await supabase
      .from("director_tasks")
      .select("*")
      .order("due_date", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) {
      debugLog.error("taskService.getAllWithArchive", error);
      throw error;
    }
    debugLog.data("Tasks loaded (with archive)", {
      count: (data || []).length,
    });
    return (data || []).map(fromDb);
  },

  // Reopen a task
  async reopen(id) {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("director_tasks")
      .update({ status: "open", completed_at: null, updated_at: now })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      debugLog.error("taskService.reopen", error);
      throw error;
    }
    debugLog.action("Task reopened", { id });
    return fromDb(data);
  },

  // Remove a task. If the target is an UNFINISHED recurring instance,
  // the parent series is deactivated as a side effect — matches the
  // blueprint decision: deleting an open recurring task means "I'm
  // done with this series," not just "I'm skipping today."
  async remove(id) {
    const { data: existing, error: fetchErr } = await supabase
      .from("director_tasks")
      .select("id, status, recurrence_id")
      .eq("id", id)
      .maybeSingle();
    if (fetchErr) {
      debugLog.error("taskService.remove(fetch)", fetchErr);
      // Fall through to attempt delete anyway — worst case the
      // delete fails too and surfaces a real error to the caller.
    }

    const { error } = await supabase
      .from("director_tasks")
      .delete()
      .eq("id", id);

    if (error) {
      debugLog.error("taskService.remove", error);
      throw error;
    }

    if (existing && existing.status === "open" && existing.recurrence_id) {
      try {
        await this.pauseRecurrence(existing.recurrence_id);
      } catch (pauseErr) {
        debugLog.error("taskService.remove(pauseRecurrence)", pauseErr);
        // Non-fatal — the task delete succeeded.
      }
    }
    debugLog.action("Task removed", {
      id,
      pausedSeries: !!(existing && existing.status === "open" && existing.recurrence_id),
    });
  },

  // Auto-complete open auto-tasks for a member + move combo
  // Used when a director advances past a portfolio move stage
  async autoCompleteByMove(memberId, moveId) {
    const now = new Date().toISOString();
    const { data, error } = await supabase
      .from("director_tasks")
      .update({ status: "completed", completed_at: now, updated_at: now })
      .eq("member_id", memberId)
      .eq("source_move", moveId)
      .eq("status", "open")
      .eq("is_auto", true)
      .select();

    if (error) {
      debugLog.error("taskService.autoCompleteByMove", error);
      // Non-critical — don't throw, just log
      return [];
    }
    if (data && data.length > 0) {
      debugLog.action("Auto-completed tasks", { memberId, moveId, count: data.length });
    }
    return (data || []).map(fromDb);
  },
};
